import React from "react";
import { useLang } from "../../context/LanguageContext";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { buildWaLink, IMAGES } from "../../lib/constants";
import { ArrowDown, Star } from "lucide-react";

export const Hero = () => {
  const { t, lang } = useLang();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-stone-950"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={IMAGES.suite1}
          alt="Suite maXimo & Annette Rooms — Bari Palese"
          className="w-full h-full object-cover slow-zoom"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/75" />
      </div>

      {/* Top side ornament */}
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black/40 to-transparent pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 min-h-[100svh] flex flex-col">
        <div className="flex-1 max-w-7xl w-full mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-20 flex flex-col justify-end md:justify-center">
          {/* Rating pill */}
          <div className="mb-8 inline-flex items-center gap-3 self-start bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2 rounded-full">
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-3.5 h-3.5 fill-amber-300 text-amber-300" />
              ))}
            </div>
            <span className="text-xs uppercase tracking-widest text-white/90">
              8.6 · 143 {lang === "it" ? "recensioni" : "reviews"}
            </span>
          </div>

          <span
            className="text-xs md:text-sm uppercase tracking-[0.35em] text-amber-200/90 mb-6"
            data-testid="hero-overline"
          >
            — {t.hero.overline}
          </span>

          <h1
            className="font-heading text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight"
            data-testid="hero-title"
          >
            Annette Rooms
            <br />
            <span className="italic font-light text-amber-100/95">&amp; Suite maXimo</span>
          </h1>

          <p
            className="mt-8 max-w-xl text-lg md:text-xl font-light text-white/85 leading-relaxed"
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 items-stretch sm:items-start">
            <a
              href={buildWaLink(
                lang === "it"
                  ? "Ciao Annette Rooms, vorrei informazioni sulla disponibilità."
                  : "Hello Annette Rooms, I'd like information on availability."
              )}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-cta"
              className="group inline-flex items-center justify-center gap-3 bg-[#25D366] text-white px-6 sm:px-8 py-4 sm:py-5 text-xs uppercase tracking-[0.25em] font-medium hover:bg-[#1faa52] transition-all duration-300 shadow-2xl hover:-translate-y-0.5"
            >
              <WhatsAppIcon className="w-5 h-5" />
              {t.hero.cta}
            </a>

            <a
              href="#suite"
              data-testid="hero-suite-cta"
              className="inline-flex items-center justify-between gap-3 text-amber-100 border border-amber-300/50 px-6 sm:px-8 py-4 sm:py-5 text-xs uppercase tracking-[0.25em] hover:bg-amber-200/15 transition-all duration-300"
            >
              <span>Suite maXimo</span>
              <ArrowDown className="w-4 h-4" />
            </a>

            <a
              href="#rooms"
              data-testid="hero-rooms-cta"
              className="inline-flex items-center justify-between gap-3 text-white border border-white/40 px-6 sm:px-8 py-4 sm:py-5 text-xs uppercase tracking-[0.25em] hover:bg-white hover:text-stone-900 transition-all duration-300"
            >
              <span>{t.nav.rooms}</span>
              <ArrowDown className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="relative z-10 border-t border-white/20 bg-black/20 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between text-white/70 text-[11px] uppercase tracking-[0.3em]">
            <span>{t.hero.scroll}</span>
            <span className="hidden md:block">Via Domenico Renna 14 · Bari</span>
            <span className="flex items-center gap-2">
              <span className="w-10 h-px bg-white/40" />
              01 / 08
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};
